import { z } from 'zod'
import { FinancialCacheSchema } from '@shared/contracts/financial-cache'
import { PaymentMethodSchema, CurrencySchema } from '@shared/contracts/enums'
import { BalanceSummarySchema } from '@entities/wallet'
import { MonetaryValueSchema } from '@shared/lib/money/money'

export const RevenuePointSchema = z.object({
  label: z.string(),
  venda: MonetaryValueSchema,
  recebido: MonetaryValueSchema,
  previousVenda: MonetaryValueSchema.optional(),
  previousRecebido: MonetaryValueSchema.optional(),
})

export const TopProductSchema = z.object({
  id: z.string(),
  name: z.string(),
  salesCount: z.number(),
  revenue: MonetaryValueSchema,
})

export const DashboardRangeSchema = z.enum(['7d', '30d', 'month', 'custom'])
export const DashboardViewSchema = z.enum(['all', 'new', 'renewals'])

export const DashboardMoneyStatSchema = z.object({
  value: MonetaryValueSchema,
  deltaPct: z.number().optional(),
  deltaPositive: z.boolean().optional(),
})

export const DashboardCountStatSchema = z.object({
  value: z.number(),
  deltaPct: z.number().optional(),
  deltaPositive: z.boolean().optional(),
})

export const DashboardRateStatSchema = z.object({
  value: z.number(),
  deltaPp: z.number().optional(),
  deltaPositive: z.boolean().optional(),
})

export const DashboardFaturamentoSchema = z.object({
  liquido: DashboardMoneyStatSchema,
  bruto: MonetaryValueSchema,
  taxas: MonetaryValueSchema,
})

export const FinancialSourceSchema = z.object({
  gross: MonetaryValueSchema,
  net: MonetaryValueSchema,
  charges: z.number(),
})

export const FinancialLossSchema = z.object({
  count: z.number(),
  amount: MonetaryValueSchema,
  ratePct: z.number(),
})

export const FinancialSummarySchema = z.object({
  sources: z.object({
    oneTime: FinancialSourceSchema,
    subscriptionInitial: FinancialSourceSchema,
    renewals: FinancialSourceSchema,
    subscriptions: FinancialSourceSchema,
    total: FinancialSourceSchema,
  }),
  losses: z.object({
    fxLosses: FinancialLossSchema.optional(),
    refunds: FinancialLossSchema,
    preventedRefunds: FinancialLossSchema,
    chargebacks: FinancialLossSchema,
    chargebackFees: z.object({
      count: z.number(),
      generated: MonetaryValueSchema,
      paid: MonetaryValueSchema,
    }),
    total: MonetaryValueSchema,
  }),
  result: MonetaryValueSchema,
})

export const PaymentMethodBreakdownSchema = z.object({
  method: z.union([PaymentMethodSchema, z.literal('UNKNOWN')]),
  amount: MonetaryValueSchema,
  pct: z.number(),
})

export const PendingPixSchema = z.object({
  count: z.number(),
  amount: MonetaryValueSchema,
})

export const OriginSliceSchema = z.object({
  salesCount: z.number(),
  amount: MonetaryValueSchema,
  pct: z.number(),
})

export const OriginBreakdownSchema = z.object({
  direct: OriginSliceSchema,
  affiliate: OriginSliceSchema,
  topAffiliates: z.array(
    z.object({
      affiliateId: z.string(),
      salesCount: z.number(),
      revenue: MonetaryValueSchema,
    }),
  ),
})

export const StateRankingSchema = z.object({
  state: z.string(),
  salesCount: z.number(),
  revenue: MonetaryValueSchema,
})

export const DashboardIndicatorsSchema = z.object({
  salesCount: DashboardCountStatSchema,
  chargeCount: DashboardCountStatSchema,
  cardApprovalPct: DashboardRateStatSchema,
  refundRatePct: DashboardRateStatSchema,
  chargebackRatePct: DashboardRateStatSchema,
})

export const GrossSalesPointSchema = z.object({
  label: z.string(),
  amount: MonetaryValueSchema,
  previousAmount: MonetaryValueSchema,
})

export const DashboardWindowSchema = z.object({
  range: DashboardRangeSchema,
  from: z.string().optional(),
  to: z.string().optional(),
})

export const DashboardCurrencyOverviewSchema = z.object({
  indicators: DashboardIndicatorsSchema,
  grossSalesSeries: z.array(GrossSalesPointSchema),
  awardContribution: MonetaryValueSchema,
  faturamento: DashboardFaturamentoSchema,
  recebido: DashboardMoneyStatSchema,
  ticketMedio: DashboardMoneyStatSchema,
  revenueSeries: z.array(RevenuePointSchema),
  topProducts: z.array(TopProductSchema),
  payMethods: z.array(PaymentMethodBreakdownSchema),
  pendingPix: PendingPixSchema,
  originBreakdown: OriginBreakdownSchema,
  statesRanking: z.array(StateRankingSchema),
  financial: FinancialSummarySchema,
})

export const DashboardOverviewSchema = z.object({
  cache: FinancialCacheSchema.optional(),
  window: DashboardWindowSchema,
  primaryCurrency: CurrencySchema,
  currencies: z.array(CurrencySchema),
  byCurrency: z.record(z.string(), DashboardCurrencyOverviewSchema),
  global: DashboardIndicatorsSchema,
  saldo: z.array(BalanceSummarySchema),
})

export const DashboardLiveSchema = z.object({
  generatedAt: z.string(),
  window: DashboardWindowSchema,
  periodOpen: z.boolean(),
  byCurrency: z.record(z.string(), z.object({ grossSalesSeries: z.array(GrossSalesPointSchema) })),
})

export const RecurrenceWaterfallSchema = z.object({
  label: z.string(),
  count: z.number(),
  amount: MonetaryValueSchema,
})

export const RecurrenceDunningSchema = z.object({
  method: z.union([PaymentMethodSchema, z.literal('UNKNOWN')]),
  count: z.number(),
  amount: MonetaryValueSchema,
  pct: z.number(),
})

export const DashboardCurrencyRecurrenceSchema = z.object({
  mrrContratado: DashboardMoneyStatSchema,
  realizado: DashboardMoneyStatSchema,
  gapInadimplencia: MonetaryValueSchema,
  activeSubscriptions: z.number(),
  netNew: z.number(),
  churn: z.object({
    subscriptions: z.number(),
    revenue: MonetaryValueSchema,
    logoPct: z.number(),
  }),
  waterfall: z.array(RecurrenceWaterfallSchema),
  dunning: z.array(RecurrenceDunningSchema),
  projection: z.object({
    from: z.string(),
    to: z.string(),
    charged: z.object({
      subscriptions: z.number(),
      charges: z.number(),
      amount: MonetaryValueSchema,
    }),
    scheduled: z.object({
      subscriptions: z.number(),
      charges: z.number(),
      amount: MonetaryValueSchema,
    }),
    recovery: z.object({
      subscriptions: z.number(),
      charges: z.number(),
      amount: MonetaryValueSchema,
    }),
    remaining: z.object({
      subscriptions: z.number(),
      charges: z.number(),
      amount: MonetaryValueSchema,
    }),
    potential: MonetaryValueSchema,
  }),
  ltv: z.object({
    available: z.boolean(),
    value: MonetaryValueSchema.nullable(),
    arpu: MonetaryValueSchema,
    churnPct: z.number(),
    historyMonths: z.number(),
  }),
})

export const DashboardRecurrenceSchema = z.object({
  cache: FinancialCacheSchema.optional(),
  window: z.object({
    range: DashboardRangeSchema,
    from: z.string().optional(),
    to: z.string().optional(),
    previousFrom: z.string().optional(),
    previousTo: z.string().optional(),
  }),
  primaryCurrency: CurrencySchema,
  currencies: z.array(CurrencySchema),
  byCurrency: z.record(z.string(), DashboardCurrencyRecurrenceSchema),
  global: z.object({
    activeSubscriptions: z.number(),
    netNew: z.number(),
    churnSubscriptions: z.number(),
  }),
})

export const AwardStatusSchema = z.enum(['PENDENTE', 'ATENDIDO'])

export const AwardTierSchema = z.object({
  id: z.string(),
  nome: z.string(),
  target: MonetaryValueSchema,
  missing: MonetaryValueSchema,
  reached: z.boolean(),
  requested: z.boolean(),
  requestStatus: AwardStatusSchema.optional(),
})

export const AwardsPerformanceSchema = z.object({
  total: MonetaryValueSchema,
  tiers: z.array(AwardTierSchema),
  nextTier: AwardTierSchema.nullable(),
})

export const AwardRequestSchema = z.object({
  id: z.string(),
  usuarioId: z.string(),
  metaId: z.string(),
  valorMeta: MonetaryValueSchema,
  status: AwardStatusSchema,
  endereco: z.record(z.string(), z.unknown()),
  linkRastreio: z.string().optional(),
  codigoRastreio: z.string().optional(),
  dataSolicitacao: z.string(),
  atendidoAt: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const AwardsListSchema = z.object({
  items: z.array(AwardRequestSchema),
})

export const AwardRequestResponseSchema = z.object({
  message: z.string(),
  item: AwardRequestSchema,
})

export type DashboardOverview = z.infer<typeof DashboardOverviewSchema>
export type DashboardRecurrence = z.infer<typeof DashboardRecurrenceSchema>
export type DashboardLive = z.infer<typeof DashboardLiveSchema>
export type GrossSalesPoint = z.infer<typeof GrossSalesPointSchema>
export type DashboardRange = z.infer<typeof DashboardRangeSchema>
export type DashboardView = z.infer<typeof DashboardViewSchema>
export type DashboardMoneyStat = z.infer<typeof DashboardMoneyStatSchema>
export type DashboardCountStat = z.infer<typeof DashboardCountStatSchema>
export type DashboardRateStat = z.infer<typeof DashboardRateStatSchema>
export type DashboardCurrencyOverview = z.infer<typeof DashboardCurrencyOverviewSchema>
export type FinancialSummary = z.infer<typeof FinancialSummarySchema>
export type TopProduct = z.infer<typeof TopProductSchema>
export type RevenuePoint = z.infer<typeof RevenuePointSchema>
export type PaymentMethodBreakdown = z.infer<typeof PaymentMethodBreakdownSchema>
export type PendingPix = z.infer<typeof PendingPixSchema>
export type OriginBreakdown = z.infer<typeof OriginBreakdownSchema>
export type StateRanking = z.infer<typeof StateRankingSchema>
export type DashboardCurrencyRecurrence = z.infer<typeof DashboardCurrencyRecurrenceSchema>
export type AwardTier = z.infer<typeof AwardTierSchema>
export type AwardsPerformance = z.infer<typeof AwardsPerformanceSchema>
export type AwardRequest = z.infer<typeof AwardRequestSchema>
export type AwardsList = z.infer<typeof AwardsListSchema>
export type AwardRequestResponse = z.infer<typeof AwardRequestResponseSchema>
